import React from 'react';
import { 
  Card, 
  Box, 
  Typography, 
  Chip, 
  Grid, 
  LinearProgress, 
  Divider, 
  alpha, 
  useTheme,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

/**
 * Component to display OpenSearch cluster health metrics
 * 
 * @param {Object} props
 * @param {Object} props.clusterHealth - Cluster health data
 * @param {boolean} props.loading - Loading state
 * @param {function} props.onRefresh - Function to refresh data
 */
const OpenSearchClusterHealth = ({ clusterHealth, loading = false, onRefresh }) => {
  const theme = useTheme();
  
  if (loading) {
    return (
      <Card
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
          backdropFilter: 'blur(8px)',
          background: alpha(theme.palette.background.paper, 0.8),
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" gutterBottom>Cluster Health</Typography>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            Loading cluster health data...
          </Typography>
        </Box>
      </Card>
    );
  }
  
  if (!clusterHealth) {
    return (
      <Card
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
          backdropFilter: 'blur(8px)',
          background: alpha(theme.palette.background.paper, 0.8),
        }}
      >
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.6, mb: 1 }} />
          <Typography variant="h6" gutterBottom>Cluster Health</Typography>
          <Typography variant="body2" color="text.secondary">
            No cluster health data available
          </Typography>
        </Box>
      </Card>
    );
  }
  
  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    switch (status.toLowerCase()) {
      case 'green':
        return 'success';
      case 'yellow':
        return 'warning';
      case 'red':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const getStatusIcon = (status) => {
    if (!status) return <InfoIcon fontSize="small" />;
    
    switch (status.toLowerCase()) {
      case 'green':
        return <CheckIcon fontSize="small" />;
      case 'yellow':
        return <WarningIcon fontSize="small" />;
      case 'red':
        return <ErrorIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        backdropFilter: 'blur(8px)',
        background: alpha(theme.palette.background.paper, 0.8),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            {clusterHealth.cluster_name || 'OpenSearch Cluster'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={clusterHealth.status || 'Unknown'} 
              color={getStatusColor(clusterHealth.status)}
              icon={getStatusIcon(clusterHealth.status)}
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
            <Typography variant="body2" color="text.secondary">
              {clusterHealth.region || ''}
            </Typography>
          </Box>
        </Box>
        {onRefresh && (
          <IconButton size="small" onClick={onRefresh} aria-label="Refresh cluster health">
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      
      <Divider sx={{ my: 2, opacity: 0.6 }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Nodes
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">
                  {clusterHealth.number_of_nodes || 0}
                </Typography>
                <Tooltip title="Data nodes">
                  <Chip 
                    label={`${clusterHealth.number_of_data_nodes || 0} data`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Shards
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Tooltip title="Active primary shards">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Primary:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {clusterHealth.active_primary_shards || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Total active shards">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Total:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {clusterHealth.active_shards || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Shard Status
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Tooltip title="Relocating shards">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Relocating:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {clusterHealth.relocating_shards || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Initializing shards">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Initializing:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {clusterHealth.initializing_shards || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Tooltip title="Unassigned shards">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Unassigned:
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={clusterHealth.unassigned_shards > 0 ? 'error' : 'inherit'}
                      >
                        {clusterHealth.unassigned_shards || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Delayed unassigned shards">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Delayed:
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={clusterHealth.delayed_unassigned_shards > 0 ? 'warning.main' : 'inherit'}
                      >
                        {clusterHealth.delayed_unassigned_shards || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tasks
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Tooltip title="Pending tasks">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Pending:
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={clusterHealth.number_of_pending_tasks > 0 ? 'warning.main' : 'inherit'}
                      >
                        {clusterHealth.number_of_pending_tasks || 0}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Max task wait time (ms)">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="medium" mr={1}>
                        Max wait:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {clusterHealth.task_max_waiting_in_queue_millis || 0}ms
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
};

export default OpenSearchClusterHealth; 